import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Typography } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import MDBox from "components/MDBox";
import ProfileSearch from "./ProfileSearch";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import ConnectOwnProfile from "layouts/dashboard/components/ConnectOwnProfile";
import WorkspaceService from "services/workspace-service";

import UpgradePopup from "layouts/components/Upgrade";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]
import { usePermissions } from "context"; // [NEW]

const Toolbar = ({ searchQuery, setSearchQuery }) => {
  const navigate = useNavigate();
  const [isTransparent, setIsTransparent] = useState(true);
  const [navHeight, setNavHeight] = useState(75);
  const [showConnectOwnProfile, setShowConnectOwnProfile] = useState(false);

  // [NEW] Usage Enforcement State
  const [usageRaw, setUsageRaw] = useState(null);

  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State

  // [NEW] RBAC State
  const [showRBAC, setShowRBAC] = useState(false);
  const { hasPermission, requiredRoles } = usePermissions();

  // ✅ Observe navbar height dynamically
  useEffect(() => {
    const navbarEl = document.querySelector("header.MuiAppBar-root");
    if (navbarEl) setNavHeight(navbarEl.offsetHeight);

    const resizeObserver = new ResizeObserver(() => {
      if (navbarEl) setNavHeight(navbarEl.offsetHeight);
    });

    if (navbarEl) resizeObserver.observe(navbarEl);
    return () => resizeObserver.disconnect();
  }, []);

  // [NEW] Fetch Usage Logic
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();
        if (!entitlements) return;

        setUsageRaw({
          profilesUsed: entitlements.usage?.profiles || 0,
          profilesLimit: entitlements.limits?.profiles || 0,
          currentPlan: entitlements.plan || "Basic"
        });
      } catch (error) {
        console.error("Error fetching usage details:", error);
      }
    };
    fetchUsageData();
  }, []);

  // ✅ Optional scroll shadow control
  useEffect(() => {
    const handleScroll = () => setIsTransparent(window.scrollY === 0);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ Open/close handlers
  const handleAddOwnProfileClick = () => {
    // [NEW] RBAC Check
    if (!hasPermission("stash:manage")) {
      setShowRBAC(true);
      return;
    }

    // [NEW] Live Demo Restriction
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setShowDemoPopup(true);
      return;
    }

    // [NEW] Check Usage Limit
    if (usageRaw) {
      const { profilesUsed, profilesLimit } = usageRaw;
      if (Number(profilesUsed) >= Number(profilesLimit)) {
        setShowUpgrade(true);
        return;
      }
    }
    setShowConnectOwnProfile(true);
  };
  const handleCloseConnectOwnProfile = () => setShowConnectOwnProfile(false);

  const handleAddCompetitorProfileClick = () => {
    // [NEW] RBAC Check
    if (!hasPermission("stash:manage")) {
      setShowRBAC(true);
      return;
    }

    navigate("/platform-scout");
  };

  return (
    <>
      <MDBox
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap="10px"
        sx={(theme) => ({
          position: "sticky",
          top: `${navHeight - 1}px`,
          zIndex: 999,
          backgroundColor: "#ffffff",
          boxShadow: isTransparent ? "none" : "0 4px 12px rgba(0, 0, 0, 0.2)",
          padding: "8px 16px",
          borderRadius: "8px",
          transition:
            "box-shadow 0.3s ease, background-color 0.3s ease, top 0.3s ease",
        })}
      >
        {/* 🔍 Profile Search (Left Side) */}
        <MDBox flexGrow={1}>
          <ProfileSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </MDBox>
        {/* ➕ Connect Section (Right Side) */}
        <MDBox display="flex" flexDirection="column" alignItems="center">
          <MDTypography
            variant="button"
            fontWeight="medium"
            color="text"
            sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
          >
            Connect
          </MDTypography>

          <MDBox
            bgColor="#f7f7f7"
            sx={{
              borderRadius: "16px",
              backgroundColor: "#f0f2f5",
              padding: "14px",
              display: "flex",
              flexDirection: "row", // ✅ place cards side by side
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* 🟢 Add Own Profile */}
            <Card
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 1,
                padding: "4px 18px",
                borderRadius: "12px",
                backgroundColor: "grey.100",
                boxShadow: 1,
                border: "1px solid #ddd",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
                "&:active": {
                  backgroundColor: "grey.400",
                  boxShadow: 6,
                },
              }}
              onClick={handleAddOwnProfileClick}
            >
              <IconButton color="primary" sx={{ p: 0 }}>
                <AddCircleIcon fontSize="large" />
              </IconButton>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="textPrimary"
                ml={1}
              >
                Add Own Profile
              </Typography>
            </Card>

            {/* 🔵 Add Competitor Profile */}
            <Card
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 1,
                padding: "4px 18px",
                borderRadius: "12px",
                backgroundColor: "grey.100",
                boxShadow: 1,
                border: "1px solid #ddd",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
                "&:active": {
                  backgroundColor: "grey.400",
                  boxShadow: 6,
                },
              }}
              onClick={handleAddCompetitorProfileClick}
            >
              <IconButton color="primary" sx={{ p: 0 }}>
                <AddCircleIcon fontSize="large" />
              </IconButton>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="textPrimary"
                ml={1}
              >
                Add Competitor Profile
              </Typography>
            </Card>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* 🔘 ConnectOwnProfile Modal */}
      {showConnectOwnProfile && (
        <ConnectOwnProfile
          open={showConnectOwnProfile}
          onClose={handleCloseConnectOwnProfile}
        />
      )}

      {/* [NEW] Upgrade Popup */}
      <UpgradePopup
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        type="usage"
        featureName="Tracked Profiles"
        planRequired="Higher Plan"
        currentPlan={usageRaw?.currentPlan}

        usageLimit={usageRaw?.profilesLimit}
      />

      {/* [NEW] Live Demo Restriction Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName="Connecting Your Profile"
      />

      {/* [NEW] RBAC Restriction Popup */}
      <UpgradePopup
        open={showRBAC}
        onClose={() => setShowRBAC(false)}
        type="rbac"
        featureName="Connecting Profiles"
        roleRequired={requiredRoles["stash:manage"] || "Manager"}
      />
    </>
  );
};

export default Toolbar;
